import React, { useState } from "react";
import { Box, Button, Chip, IconButton } from "@mui/material";
import {
  CheckCircleOutline,
  Add,
  InfoOutlined,
  ModeEditOutlineTwoTone,
} from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import BaseSelect from "components/Shared/Base/BaseSelect";
import CollectionModal from "../Collection/CollectionComponents/CollectionModal.jsx";
import { useLazyListShipmentQuery } from "store/services/shipmentApi";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";

const getStatusStyle = (status) => {
  const styles = {
    Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
    Collected: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
    Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const ContractorShipmentListScreen = () => {
  const { isVendor, vendorId, isContractor, isCustomer, loggedInUser } =
    useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [sortOrder, setSortOrder] = React.useState("createdAt:-1");
  const [priority, setPriority] = React.useState("");
  const [deliveryTypes, setDeliveryTypes] = React.useState("");
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();

  const urlTab = searchParams.get("tab");
  const defaultTab =
    loggedInUser?.typeRefSource === "Contractor" ? "Pending" : "Active";
  const activeTab = urlTab || defaultTab;

  const keyword = searchParams.get("search") || "";
  const breadcrumbs = [{ title: "Shipment " }];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.shipmentId,
      cell: (row) => row?.shipmentId,
      width: "150px",
    },
    {
      name: "Sender",
      selector: (row) => row?.customer?.customerID || row?.vendor?.name,
      cell: (row) => (
        <Box
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "auto",
          }}
        >
          {row?.customer?.customerID
            ? `${row.customer?.customerID}`
            : row?.vendor?.name
            ? `${row.vendor?.name}`
            : "N/A"}
        </Box>
      ),
      omit: isVendor,
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.customerID,
      cell: (row) => (
        <div>
          <span className="text-sm" style={{ whiteSpace: "nowrap"}}>
            {row?.receiver?.customerUser?.name}
          </span>{" "}
          <br />
          <span className="text-sm  text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name,
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name,
    },
    {
      name: "Pickup Time",
      selector: (row) => <AppTableDateTime date={row?.shipmentPickupTime} />,
    },
    {
      name: "Delivery Time",
      selector: (row) =>
        row?.shipmentDeliveryTime ? (
          <AppTableDateTime date={row.shipmentDeliveryTime} />
        ) : (
          "Not delivered at"
        ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            variant="outlined"
            sx={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              height: "17px",
              borderRadius: "12px",
              width: "auto",
              padding: "0",
              border: "none",
              "& .MuiChip-label": {
                fontSize: "0.6rem",
                padding: "3px 5px",
                lineHeight: "14px",
              },
            }}
          />
        </Box>
      ),
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/shipment"
          : isCustomer
          ? "/app/customer/shipment"
          : "/app/crm/shipment";
        const isEditDisabled =
          (isVendor || isCustomer) && row?.status !== "Pending";

        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            {isContractor ? (
              <IconButton
                size="small"
                aria-label="Approve Shipment"
                color="primary"
                onClick={() => row && handleApproveClick(row)}
                disabled={!row}
              >
                <CheckCircleOutline />
              </IconButton>
            ) : (
              <>
                <Link to={`${basePath}/${row?._id}/detail`}>
                  <IconButton
                    size="small"
                    aria-label="View Shipment"
                    color="primary"
                  >
                    <InfoOutlined />
                  </IconButton>
                </Link>
                <Link
                  to={isEditDisabled ? "#" : `${basePath}/${row?._id}/edit`}
                  style={{ pointerEvents: isEditDisabled ? "none" : "auto" }}
                >
                  <IconButton
                    size="small"
                    aria-label="Edit Shipment"
                    disabled={isEditDisabled}
                    sx={{ opacity: isEditDisabled ? 0.5 : 1 }}
                  >
                    <ModeEditOutlineTwoTone />
                  </IconButton>
                </Link>
              </>
            )}
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipment, { data: shipmentRes, isLoading, isFetching, error }] =
    useLazyListShipmentQuery();

  React.useEffect(() => {
    const [sortedBy, sortDirection] = sortOrder.split(":");
    fetchShipment({
      active:
        activeTab === "Active"
          ? true
          : activeTab === "Inactive"
          ? false
          : undefined,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
      ...(priority && { priorityTypes: priority }),
      ...(deliveryTypes && { types: deliveryTypes }),
      ...(isVendor && { vendorId }),
      ...(isCustomer && { customerId: loggedInUser?.typeRef }),
      ...(activeTab !== "Active" &&
        activeTab !== "Inactive" && { status: activeTab }),
    });
  }, [
    activeTab,
    currentPage,
    deliveryTypes,
    fetchShipment,
    keyword,
    perPage,
    priority,
    sortOrder,
    isVendor,
    vendorId,
    isCustomer,
    loggedInUser?.typeRef,
  ]);

  const handleApproveClick = (shipment) => {
    setSelectedShipment(shipment);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedShipment(null);
  };

  useAppMeta(`${activeTab} Shipment`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs defaultValue={activeTab} />
        <TableFilter
          searchLabel="Shipment ID"
          disabled={isLoading || isFetching}
          initialFilters={2}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
          <BaseSelect
            fullWidth
            label="Delivery Types"
            value={deliveryTypes}
            onChange={(e) => setDeliveryTypes(e.target.value)}
            options={[
              { label: "Express", value: "Express" },
              { label: "Regular", value: "Regular" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
          <BaseSelect
            fullWidth
            label="Shipment Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            options={[
              { label: "Medium", value: "Medium" },
              { label: "High", value: "High" },
              { label: "Low", value: "Low" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={shipmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
        {/* Added Collection Modal */}
        {selectedShipment && (
          <CollectionModal
            open={modalOpen}
            onClose={handleModalClose}
            shipment={selectedShipment}
          />
        )}
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ContractorShipmentListScreen;

const PageActions = () => {
  const { isVendor, isCustomer, isContractor } = useAuth();
  return (
    <>
      {!isContractor && (
        <Link
          to={
            isVendor
              ? "/app/vendor/shipment/create"
              : isCustomer
              ? "/app/customer/shipment/create"
              : "/app/crm/shipment/create"
          }
        >
          <Button startIcon={<Add />} variant="contained" color="primary">
            Add Shipment
          </Button>
        </Link>
      )}
    </>
  );
};
